import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { AppShell } from '@/components/AppShell';
import { useBusinesses } from '@/context/BusinessContext';
import {
  ApiError,
  createTransaction,
  getAccounts,
  getTaxCodes,
  getTransactions,
  type Account,
  type CashBankOperation,
  type PaymentMethod,
  type TaxCode,
  type Transaction,
  type TransactionEntry,
  type TransactionHeader,
} from '@/lib/api';

// ---- money / date helpers -------------------------------------------------

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Parse a dollars string (e.g. "100", "100.5", "100.50") to integer cents. */
function parseDollarsToCents(input: string): number | null {
  const s = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2));
}

/** Today's calendar date in SI-local time (UTC+11), to match the reference clock. */
function todayISO(): string {
  return new Date(Date.now() + 11 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// ---- operation metadata ---------------------------------------------------

type Direction = 'in' | 'out' | 'transfer';

interface OpMeta {
  value: CashBankOperation;
  label: string;
  group: string;
  direction: Direction;
  hint: string;
  amountHint: string;
  /** null hides the counterparty field (e.g. an internal transfer). */
  counterpartyLabel: string | null;
}

const OPERATIONS: OpMeta[] = [
  {
    value: 'TRANSFER',
    label: 'Transfer between your accounts',
    group: 'Move money',
    direction: 'transfer',
    hint: 'Move money between two of your own cash or bank accounts. This does not change your profit — only where the money sits.',
    amountHint: 'The amount to move.',
    counterpartyLabel: null,
  },
  {
    value: 'CAPITAL_IN',
    label: 'Money put into the business',
    group: 'Owner & funding',
    direction: 'in',
    hint: "Records the owner putting personal money into the business.",
    amountHint: 'The amount paid in.',
    counterpartyLabel: 'Source',
  },
  {
    value: 'DRAWINGS',
    label: 'Money taken out for the owner',
    group: 'Owner & funding',
    direction: 'out',
    hint: "Records money taken out of the business for the owner's personal use.",
    amountHint: 'The amount taken out.',
    counterpartyLabel: null,
  },
  {
    value: 'LOAN_RECEIVED',
    label: 'Loan received',
    group: 'Owner & funding',
    direction: 'in',
    hint: 'Records loan money coming in. The amount is recorded as something the business owes.',
    amountHint: 'The amount received.',
    counterpartyLabel: 'Lender',
  },
  {
    value: 'LOAN_REPAYMENT',
    label: 'Loan repayment',
    group: 'Owner & funding',
    direction: 'out',
    hint: 'Records a loan repayment. Enter the total paid, and how much of it (if any) was interest.',
    amountHint: 'The total paid (principal plus interest).',
    counterpartyLabel: 'Lender',
  },
  {
    value: 'BANK_FEE',
    label: 'Bank fee or charge',
    group: 'Bank items',
    direction: 'out',
    hint: 'Records a bank fee or charge as an expense.',
    amountHint: 'The fee charged.',
    counterpartyLabel: null,
  },
  {
    value: 'INTEREST_RECEIVED',
    label: 'Interest received',
    group: 'Bank items',
    direction: 'in',
    hint: 'Records interest the bank paid you, as income.',
    amountHint: 'The interest received.',
    counterpartyLabel: null,
  },
  {
    value: 'BILL_PAYMENT',
    label: 'Pay a supplier bill',
    group: 'Pay a bill',
    direction: 'out',
    hint: 'Pays a supplier bill recorded earlier on credit. Withholding tax, if any, is applied now.',
    amountHint: 'What the supplier receives. Withholding, if any, is added below.',
    counterpartyLabel: 'Supplier',
  },
];

const OP_GROUP_ORDER = ['Move money', 'Owner & funding', 'Bank items', 'Pay a bill'];

const OP_SHORT_LABELS: Record<string, string> = {
  TRANSFER: 'Transfer',
  CAPITAL_IN: 'Capital in',
  DRAWINGS: 'Drawings',
  LOAN_RECEIVED: 'Loan received',
  LOAN_REPAYMENT: 'Loan repayment',
  BANK_FEE: 'Bank fee',
  INTEREST_RECEIVED: 'Interest received',
  BILL_PAYMENT: 'Bill payment',
};

/**
 * Only offer operations valid for this entity type — the engine rejects the
 * rest, but hiding them avoids a confusing server error. Drawings are
 * sole-trader/partnership only (a company's distributions are dividends, a
 * later pass; CCL/trust have none); capital-in does not apply to a charitable
 * trust (its incoming money is income, recorded on the Income card).
 */
function operationsForEntity(entityType: string): OpMeta[] {
  return OPERATIONS.filter((op) => {
    if (op.value === 'DRAWINGS') {
      return entityType === 'sole_trader' || entityType === 'partnership';
    }
    if (op.value === 'CAPITAL_IN') {
      return entityType !== 'charitable_trust';
    }
    return true;
  });
}

const inputClass =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';
const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300';
const cardClass =
  'rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900';

// ---- component ------------------------------------------------------------

export function CashBank() {
  const { getToken } = useAuth();
  const { activeBusinessId, activeBusiness } = useBusinesses();
  const currency = activeBusiness?.currency_code ?? 'SBD';
  const entityType = activeBusiness?.entity_type ?? 'sole_trader';

  const availableOps = useMemo(() => operationsForEntity(entityType), [entityType]);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [refsError, setRefsError] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);

  // form state
  const [operation, setOperation] = useState<CashBankOperation>('TRANSFER');
  const [amount, setAmount] = useState('');
  const [interest, setInterest] = useState('');
  const [accountId, setAccountId] = useState(''); // settlement (primary leg)
  const [toAccountId, setToAccountId] = useState(''); // counter (transfer destination)
  const [whtCodeId, setWhtCodeId] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [reference, setReference] = useState('');
  const [txnDate, setTxnDate] = useState(todayISO());

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{
    transaction: TransactionHeader;
    entries: TransactionEntry[];
  } | null>(null);

  const meta = useMemo(
    () => availableOps.find((o) => o.value === operation) ?? availableOps[0],
    [availableOps, operation],
  );

  const cashBankAccounts = useMemo(
    () => accounts.filter((a) => a.is_bank_or_cash === 1),
    [accounts],
  );
  const toOptions = useMemo(
    () => cashBankAccounts.filter((a) => a.id !== accountId),
    [cashBankAccounts, accountId],
  );

  // Withholding codes (bill payment only), grouped resident vs non-resident.
  const whtResident = useMemo(
    () => taxCodes.filter((t) => t.tax_type === 'WITHHOLDING' && !t.code.startsWith('WHT-NR-')),
    [taxCodes],
  );
  const whtNonResident = useMemo(
    () => taxCodes.filter((t) => t.tax_type === 'WITHHOLDING' && t.code.startsWith('WHT-NR-')),
    [taxCodes],
  );
  const hasWht = whtResident.length + whtNonResident.length > 0;
  const selectedWht = useMemo(
    () => taxCodes.find((t) => t.id === whtCodeId) ?? null,
    [taxCodes, whtCodeId],
  );

  const netCents = parseDollarsToCents(amount);

  // Bill-payment WHT gross-up, matching the engine.
  const isBill = operation === 'BILL_PAYMENT';
  const whtBps = isBill ? (selectedWht?.rate_basis_points ?? 0) : 0;
  const whtCents =
    isBill && selectedWht && netCents !== null && whtBps > 0 && whtBps < 10000
      ? Math.round((netCents * whtBps) / (10000 - whtBps))
      : 0;
  const grossCents = (netCents ?? 0) + whtCents;

  // Loan-repayment split.
  const isRepayment = operation === 'LOAN_REPAYMENT';
  const interestCents = isRepayment
    ? interest.trim() === ''
      ? 0
      : parseDollarsToCents(interest)
    : 0;
  const principalCents =
    netCents !== null && interestCents !== null ? netCents - interestCents : null;

  const accountLabel = useCallback(
    (id: string) => {
      const a = accounts.find((x) => x.id === id);
      return a ? `${a.code} ${a.name}` : id;
    },
    [accounts],
  );

  const loadRefs = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoadingRefs(true);
    setRefsError(null);
    try {
      const token = await getToken();
      const [accs, codes] = await Promise.all([
        getAccounts(token, activeBusinessId),
        getTaxCodes(token, activeBusinessId),
      ]);
      setAccounts(accs);
      setTaxCodes(codes);
      const firstCb = accs.find((a) => a.is_bank_or_cash === 1);
      setAccountId(firstCb?.id ?? '');
    } catch (err) {
      setRefsError(
        err instanceof ApiError ? err.message : 'Could not load the chart of accounts.',
      );
    } finally {
      setLoadingRefs(false);
    }
  }, [activeBusinessId, getToken]);

  const loadTransactions = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoadingList(true);
    try {
      const token = await getToken();
      setTransactions(await getTransactions(token, activeBusinessId, 'CASH_BANK'));
    } catch {
      // A failed list refresh is non-fatal; leave the prior list in place.
    } finally {
      setLoadingList(false);
    }
  }, [activeBusinessId, getToken]);

  useEffect(() => {
    void loadRefs();
    void loadTransactions();
  }, [loadRefs, loadTransactions]);

  // Keep the selected operation valid if the active business (entity type) changes.
  useEffect(() => {
    if (!availableOps.some((o) => o.value === operation)) {
      setOperation(availableOps[0].value);
    }
  }, [availableOps, operation]);

  // Keep the primary account pointing at a real cash/bank account.
  useEffect(() => {
    if (cashBankAccounts.length > 0 && !cashBankAccounts.some((a) => a.id === accountId)) {
      setAccountId(cashBankAccounts[0].id);
    }
  }, [cashBankAccounts, accountId]);

  // For a transfer, keep the destination valid and distinct from the source.
  useEffect(() => {
    if (operation !== 'TRANSFER') return;
    if (toOptions.length > 0 && !toOptions.some((a) => a.id === toAccountId)) {
      setToAccountId(toOptions[0].id);
    }
  }, [operation, toOptions, toAccountId]);

  const primaryAccount = useMemo(
    () => cashBankAccounts.find((a) => a.id === accountId) ?? null,
    [cashBankAccounts, accountId],
  );

  const primaryLabel =
    meta.direction === 'transfer'
      ? 'From account'
      : meta.direction === 'in'
        ? 'Into which account?'
        : 'From which account?';

  const noAccounts = cashBankAccounts.length === 0;
  const needsTwo = operation === 'TRANSFER' && cashBankAccounts.length < 2;

  const canSubmit =
    !submitting &&
    !noAccounts &&
    !needsTwo &&
    netCents !== null &&
    netCents > 0 &&
    accountId !== '' &&
    (operation !== 'TRANSFER' || (toAccountId !== '' && toAccountId !== accountId)) &&
    (!isRepayment ||
      (interestCents !== null && interestCents >= 0 && interestCents <= (netCents ?? 0)));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!activeBusinessId) return;
    if (netCents === null || netCents <= 0) {
      setSubmitError('Enter an amount greater than zero.');
      return;
    }
    if (accountId === '') {
      setSubmitError('Choose a cash or bank account.');
      return;
    }
    if (operation === 'TRANSFER' && (toAccountId === '' || toAccountId === accountId)) {
      setSubmitError('Choose two different accounts for the transfer.');
      return;
    }
    if (isRepayment && (interestCents === null || interestCents < 0 || interestCents > netCents)) {
      setSubmitError('The interest portion must be zero or more, and not exceed the total paid.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getToken();
      const result = await createTransaction(token, {
        business_id: activeBusinessId,
        module: 'CASH_BANK',
        transaction_date: txnDate,
        amount_cents: netCents,
        sale_category_id: null,
        income_account_id: null,
        expense_account_id: null,
        cash_bank_operation: operation,
        counter_account_id: operation === 'TRANSFER' ? toAccountId : null,
        interest_cents: isRepayment ? (interestCents ?? 0) : null,
        // Not used by the engine for CASH_BANK (the account ids are
        // authoritative); sent only to satisfy the shared input type.
        payment_method: (primaryAccount?.cash_bank_kind ?? 'CASH') as PaymentMethod,
        settlement_account_id: accountId,
        tax_code_id: isBill && selectedWht ? selectedWht.id : null,
        tax_inclusive: false,
        quantity: null,
        counterparty: counterparty.trim() || null,
        description: null,
        reference: reference.trim() || null,
      });
      setLastResult(result);
      setAmount('');
      setInterest('');
      setWhtCodeId('');
      setCounterparty('');
      setReference('');
      await loadTransactions();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not record the movement.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell title="Cash / Bank">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* ---- Record a movement ---- */}
        <section className="lg:col-span-3">
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Record a cash or bank movement
          </h2>

          {refsError && (
            <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
              {refsError}
            </p>
          )}

          {loadingRefs ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : noAccounts ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This business has no cash or bank accounts set up.
            </p>
          ) : (
            <form className={cardClass} onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="operation">
                    What happened?
                  </label>
                  <select
                    id="operation"
                    className={inputClass}
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as CashBankOperation)}
                  >
                    {OP_GROUP_ORDER.map((group) => {
                      const ops = availableOps.filter((o) => o.group === group);
                      if (ops.length === 0) return null;
                      return (
                        <optgroup key={group} label={group}>
                          {ops.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{meta.hint}</p>
                </div>

                <div>
                  <label className={labelClass} htmlFor="amount">
                    Amount ({currency})
                  </label>
                  <input
                    id="amount"
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {meta.amountHint}
                  </p>
                </div>

                <div>
                  <label className={labelClass} htmlFor="date">
                    Date
                  </label>
                  <input
                    id="date"
                    type="date"
                    className={inputClass}
                    value={txnDate}
                    onChange={(e) => setTxnDate(e.target.value)}
                  />
                </div>

                {/* Primary cash/bank account (source for a transfer). */}
                <div className="sm:col-span-2">
                  <span className={labelClass}>{primaryLabel}</span>
                  {cashBankAccounts.length === 1 && operation !== 'TRANSFER' ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {cashBankAccounts[0].code} {cashBankAccounts[0].name}
                    </div>
                  ) : (
                    <select
                      id="account"
                      className={inputClass}
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                    >
                      {cashBankAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Transfer destination. */}
                {operation === 'TRANSFER' && (
                  <div className="sm:col-span-2">
                    <span className={labelClass}>To account</span>
                    {needsTwo ? (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        You need at least two cash or bank accounts to make a transfer.
                      </p>
                    ) : (
                      <select
                        id="to-account"
                        className={inputClass}
                        value={toAccountId}
                        onChange={(e) => setToAccountId(e.target.value)}
                      >
                        {toOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Loan-repayment interest split. */}
                {isRepayment && (
                  <div className="sm:col-span-2">
                    <label className={labelClass} htmlFor="interest">
                      Of which interest{' '}
                      <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    <input
                      id="interest"
                      className={inputClass}
                      inputMode="decimal"
                      placeholder="0.00"
                      value={interest}
                      onChange={(e) => setInterest(e.target.value)}
                    />
                    {netCents !== null &&
                      netCents > 0 &&
                      interestCents !== null &&
                      principalCents !== null &&
                      principalCents >= 0 && (
                        <dl className="mt-2 space-y-1 rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                          <div className="flex justify-between">
                            <dt className="text-slate-500 dark:text-slate-400">
                              Principal (reduces the loan)
                            </dt>
                            <dd className="tabular-nums text-slate-800 dark:text-slate-200">
                              {currency} {formatCents(principalCents)}
                            </dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-slate-500 dark:text-slate-400">Interest (expense)</dt>
                            <dd className="tabular-nums text-slate-800 dark:text-slate-200">
                              {currency} {formatCents(interestCents)}
                            </dd>
                          </div>
                          <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-700">
                            <dt className="text-slate-700 dark:text-slate-300">Total paid</dt>
                            <dd className="tabular-nums text-slate-900 dark:text-slate-100">
                              {currency} {formatCents(netCents)}
                            </dd>
                          </div>
                        </dl>
                      )}
                  </div>
                )}

                {/* Bill-payment withholding. */}
                {isBill && hasWht && (
                  <div className="sm:col-span-2">
                    <label className={labelClass} htmlFor="wht-code">
                      Withholding tax{' '}
                      <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    <select
                      id="wht-code"
                      className={inputClass}
                      value={whtCodeId}
                      onChange={(e) => setWhtCodeId(e.target.value)}
                    >
                      <option value="">None</option>
                      {whtResident.length > 0 && (
                        <optgroup label="Resident">
                          {whtResident.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {whtNonResident.length > 0 && (
                        <optgroup label="Non-resident">
                          {whtNonResident.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {selectedWht && netCents !== null && netCents > 0 && (
                      <dl className="mt-2 space-y-1 rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                        <div className="flex justify-between">
                          <dt className="text-slate-500 dark:text-slate-400">Pays supplier</dt>
                          <dd className="tabular-nums text-slate-800 dark:text-slate-200">
                            {currency} {formatCents(netCents)}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-500 dark:text-slate-400">
                            Withholds to IRD ({selectedWht.code})
                          </dt>
                          <dd className="tabular-nums text-slate-800 dark:text-slate-200">
                            {currency} {formatCents(whtCents)}
                          </dd>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-700">
                          <dt className="text-slate-700 dark:text-slate-300">Clears from payables</dt>
                          <dd className="tabular-nums text-slate-900 dark:text-slate-100">
                            {currency} {formatCents(grossCents)}
                          </dd>
                        </div>
                      </dl>
                    )}
                  </div>
                )}

                {meta.counterpartyLabel && (
                  <div>
                    <label className={labelClass} htmlFor="counterparty">
                      {meta.counterpartyLabel}{' '}
                      <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    <input
                      id="counterparty"
                      className={inputClass}
                      value={counterparty}
                      onChange={(e) => setCounterparty(e.target.value)}
                    />
                  </div>
                )}

                <div className={meta.counterpartyLabel ? '' : 'sm:col-span-2'}>
                  <label className={labelClass} htmlFor="reference">
                    Reference <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="reference"
                    className={inputClass}
                    placeholder="auto-generated"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </div>
              </div>

              {submitError && (
                <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Recording…' : 'Record'}
              </button>
            </form>
          )}

          {lastResult && (
            <ConfirmationPanel
              currency={currency}
              transaction={lastResult.transaction}
              entries={lastResult.entries}
              accountLabel={accountLabel}
              onDismiss={() => setLastResult(null)}
            />
          )}
        </section>

        {/* ---- Recent activity ---- */}
        <section className="lg:col-span-2">
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Recent activity
          </h2>
          <div className={cardClass}>
            {loadingList && transactions.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
            ) : transactions.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No cash or bank movements recorded yet.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {transactions.map((t) => {
                  const total = t.entries.reduce((s, e) => s + e.debit_cents, 0);
                  const tag = t.cash_bank_operation
                    ? (OP_SHORT_LABELS[t.cash_bank_operation] ?? t.cash_bank_operation)
                    : 'Movement';
                  return (
                    <li
                      key={t.id}
                      className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {t.description}
                        </p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {tag} · {t.transaction_date} · {t.reference ?? '—'}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {currency} {formatCents(total)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

// ---- confirmation panel ---------------------------------------------------

function ConfirmationPanel({
  currency,
  transaction,
  entries,
  accountLabel,
  onDismiss,
}: {
  currency: string;
  transaction: TransactionHeader;
  entries: TransactionEntry[];
  accountLabel: (id: string) => string;
  onDismiss: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Recorded</p>
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            Reference {transaction.reference ?? '—'} — the double entry we posted:
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-emerald-700 transition hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-200"
        >
          ✕
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-emerald-700/70 dark:text-emerald-400/70">
            <th className="py-1 font-medium">Account</th>
            <th className="py-1 text-right font-medium">Debit</th>
            <th className="py-1 text-right font-medium">Credit</th>
          </tr>
        </thead>
        <tbody className="text-slate-800 dark:text-slate-200">
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="py-1">{accountLabel(e.account_id)}</td>
              <td className="py-1 text-right tabular-nums">
                {e.debit_cents ? `${currency} ${formatCents(e.debit_cents)}` : ''}
              </td>
              <td className="py-1 text-right tabular-nums">
                {e.credit_cents ? `${currency} ${formatCents(e.credit_cents)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
